import { FilterV2, Option, SelectDropdown, SelectStyles } from '@serodigital/ui-components';
import * as React from 'react';
import { Project } from '../../../../../shared/api-clients/_api';
import type { FilterOptions } from '../../../contexts/listFilter';
import { equinoxLabels } from '../../bee/details';
import { BeeModeDBEntries } from '../../../../../../backend/shared/types/bee';
import { ITrialDetail } from '../../bee/state/types';

interface IListFilterProps {
  setFilterOption: (filterOption: FilterOptions) => void;
  setDropdownFilters: (dropdownFilter: FilterOptions) => void;
  filterOption: FilterOptions;
  shouldDisableClearButton: boolean;
  dynamicDropDownValues: {
    configProfileVersions: string[];
    firmwareVersions: string[];
    trials: ITrialDetail[];
    beeModes: string[];
  };
}

interface FilterOption {
  id: string;
  label: string;
  options: Option[];
  selectedOptions: Option[] | null;
  onChange: (value: Option | Option[]) => void;
  multipleSelect: boolean;
  dataQa: string;
}

enum HealthcheckValues {
  Pass = 'pass',
  Fail = 'fail',
}

enum StatusValues {
  Online = 'online',
  Offline = 'offline',
}

export const ListFilter = ({
  setFilterOption,
  setDropdownFilters,
  filterOption,
  shouldDisableClearButton,
  dynamicDropDownValues,
}: IListFilterProps) => {
  const dropdownStyles: SelectStyles = {};
  const handleOnClear = () => {
    const updatedProjectFilter = {
      ...filterOption,
      project: [],
      firmwareVersion: [],
      healthcheck: undefined,
      status: undefined,
      trial: undefined,
      configProfileVersion: undefined,
      beeMode: undefined,
    };

    setFilterOption(updatedProjectFilter);
    setDropdownFilters(updatedProjectFilter);
  };

  const projectFilterOptions = () => {
    return Object.values(Project).map((value) => ({
      label: value,
      value: value,
    }));
  };

  function alterBeeModeLabel(option: Option[]) {
    return option.map((item) => {
      const enumMatch = BeeModeDBEntries[item.label.toUpperCase()];

      return {
        label:
          item.value && enumMatch
            ? `${item.label.toUpperCase()} - ${enumMatch}`
            : item.label.charAt(0).toUpperCase() + item.label.slice(1),
        value: item.value !== undefined ? item.value : null,
      };
    });
  }

  function createBeeModeOptions(): Option[] {
    const originalBeeModeLabel = dynamicDropDownValues.beeModes?.length
      ? [
          ...dynamicDropDownValues.beeModes.map((beeModeLabel) => ({
            label: beeModeLabel,
            value: beeModeLabel,
          })),
          { label: 'None/Unknown', value: null },
        ]
      : [];

    return alterBeeModeLabel(originalBeeModeLabel);
  }

  const beeModeOptions = createBeeModeOptions();

  const trialsOptions = [
    ...dynamicDropDownValues.trials.map((trial) => ({
      label: equinoxLabels[trial.name],
      value: trial.id,
    })),
    { label: 'None', value: null },
  ];

  const configProfileVersionOptions = dynamicDropDownValues.configProfileVersions?.length
    ? [
        ...dynamicDropDownValues.configProfileVersions.map((config) => ({
          label: config,
          value: config,
        })),
        { label: 'Unknown', value: null },
      ]
    : [];

  const handleProjectsOnChange = (value: Option[]) => {
    const updatedFilter = {
      ...filterOption,
      project: value.length ? Array.from(new Set<string>(value.map((e) => e.value))) : [],
    };

    setFilterOption(updatedFilter);
    setDropdownFilters(updatedFilter);
  };

  const handleHealthcheckOnChange = (value: Option) => {
    const updatedFilter = {
      ...filterOption,
      healthcheck: value !== null ? value.value === HealthcheckValues.Pass : undefined,
    };

    setFilterOption(updatedFilter);
    setDropdownFilters(updatedFilter);
  };

  const handleStatusOnChange = (value: Option) => {
    const updatedFilter = {
      ...filterOption,
      status: value !== null ? value.value === StatusValues.Online : undefined,
    };

    setFilterOption(updatedFilter);
    setDropdownFilters(updatedFilter);
  };

  const handleConfigProfileVersionOnChange = (value: Option) => {
    const updatedFilter = {
      ...filterOption,
      configProfileVersion: value !== null ? value.value : undefined,
    };

    setFilterOption(updatedFilter);
    setDropdownFilters(updatedFilter);
  };

  const handleTrialsOnChange = (value: Option) => {
    const updatedFilter = {
      ...filterOption,
      trial:
        value?.value === null
          ? null
          : dynamicDropDownValues.trials.find((item) => item.id === value?.value)?.id || undefined,
    };

    setFilterOption(updatedFilter);
    setDropdownFilters(updatedFilter);
  };

  const firmwareVersionOptions = dynamicDropDownValues.firmwareVersions?.length
    ? [
        ...dynamicDropDownValues.firmwareVersions.map((firmwareVersion) => ({
          label: firmwareVersion,
          value: firmwareVersion,
        })),
      ]
    : [];

  const handleFirmwareVersionsOnChange = (value: Option[]) => {
    const updatedFilter = {
      ...filterOption,
      firmwareVersion: value?.length ? Array.from(new Set<string>(value.map((e) => e.value))) : [],
    };

    setFilterOption(updatedFilter);
    setDropdownFilters(updatedFilter);
  };

  const selectedVersionOptions = (): Option[] =>
    React.useMemo(() => {
      return filterOption.firmwareVersion.length > 0
        ? filterOption.firmwareVersion.map((item) => ({
            label: item,
            value: item,
          }))
        : null;
    }, [filterOption.firmwareVersion]);

  const handleBeeModeOnChange = (value: Option) => {
    const updatedFilter = {
      ...filterOption,
      beeMode: value ? value.value : undefined,
    };
    setFilterOption(updatedFilter);
    setDropdownFilters(updatedFilter);
  };

  const selectedTrials = (): Option[] =>
    React.useMemo(() => {
      if (!dynamicDropDownValues.trials || filterOption.trial === undefined) {
        return null;
      }

      if (filterOption.trial === null) {
        return [{ label: 'None', value: null }];
      }
      const selectedTrail = dynamicDropDownValues.trials.find((item) => item.id === filterOption.trial);

      return selectedTrail !== undefined
        ? [{ label: equinoxLabels[selectedTrail.name], value: selectedTrail.id }]
        : null;
    }, [filterOption.trial]);

  const selectedConfigProfileVersion = (): Option[] =>
    React.useMemo(() => {
      if (!dynamicDropDownValues.configProfileVersions || filterOption.configProfileVersion === undefined) return null;

      if (filterOption.configProfileVersion === null) return [{ label: 'None', value: null }];

      const selectedConfigProfileVersion = dynamicDropDownValues.configProfileVersions.find(
        (item) => item === filterOption.configProfileVersion,
      );

      return selectedConfigProfileVersion !== undefined
        ? [{ label: selectedConfigProfileVersion, value: selectedConfigProfileVersion }]
        : null;
    }, [filterOption.configProfileVersion]);

  const selectedBeeMode = (): Option[] =>
    React.useMemo(() => {
      if (!dynamicDropDownValues.beeModes || filterOption.beeMode === undefined) return null;

      if (filterOption.beeMode === null) return [{ label: 'None', value: null }];

      const selectedBeeMode = dynamicDropDownValues.beeModes.find((item) => item === filterOption.beeMode);

      return selectedBeeMode !== undefined ? [{ label: selectedBeeMode, value: selectedBeeMode }] : null;
    }, [filterOption.beeMode]);

  const filterOptions: FilterOption[] = [
    {
      id: 'projects',
      label: 'Projects',
      options: projectFilterOptions(),
      selectedOptions:
        filterOption.project.length > 0
          ? filterOption.project.map((item) => ({
              label: Project[item],
              value: item,
            }))
          : null,
      onChange: handleProjectsOnChange,
      multipleSelect: true,
      dataQa: 'projects-filter',
    },
    {
      id: 'healthcheck',
      label: 'Health check',
      options: [
        { label: 'Pass', value: HealthcheckValues.Pass },
        { label: 'Fail', value: HealthcheckValues.Fail },
      ],
      selectedOptions:
        filterOption.healthcheck === true
          ? [{ label: 'Pass', value: HealthcheckValues.Pass }]
          : filterOption.healthcheck === false
          ? [{ label: 'Fail', value: HealthcheckValues.Fail }]
          : null,
      onChange: handleHealthcheckOnChange,
      multipleSelect: false,
      dataQa: 'healthcheck-filter',
    },
    {
      id: 'status',
      label: 'Status',
      options: [
        { label: 'Online', value: StatusValues.Online },
        { label: 'Offline', value: StatusValues.Offline },
      ],
      selectedOptions:
        filterOption.status === true
          ? [{ label: 'Online', value: StatusValues.Online }]
          : filterOption.status === false
          ? [{ label: 'Offline', value: StatusValues.Offline }]
          : null,
      onChange: handleStatusOnChange,
      multipleSelect: false,
      dataQa: 'status-filter',
    },
    {
      id: 'trials',
      label: 'Trials',
      options: trialsOptions,
      selectedOptions: selectedTrials(),
      onChange: handleTrialsOnChange,
      multipleSelect: false,
      dataQa: 'trials-filter',
    },
    {
      id: 'firmware-versions',
      label: 'Firmware Version',
      options: firmwareVersionOptions,
      selectedOptions: selectedVersionOptions(),
      onChange: handleFirmwareVersionsOnChange,
      multipleSelect: true,
      dataQa: 'firmware-version-filter',
    },
    {
      id: 'config-profile-version',
      label: 'Config Profile Version',
      options: configProfileVersionOptions,
      selectedOptions: selectedConfigProfileVersion(),
      onChange: handleConfigProfileVersionOnChange,
      multipleSelect: false,
      dataQa: 'config-profile-version-filter',
    },
    {
      id: 'bee-mode',
      label: 'Bee Mode',
      options: beeModeOptions,
      selectedOptions: selectedBeeMode(),
      onChange: handleBeeModeOnChange,
      multipleSelect: false,
      dataQa: 'bee-mode-filter',
    },
  ];

  return (
    <div className="py-20 -mt-64">
      <FilterV2
        onClear={handleOnClear}
        data-qa="hms-filter"
        disableOnClearButton={shouldDisableClearButton}
        className="sero-block sero-w-full"
        dropdownStyles="sero-flex-end"
      >
        {filterOptions.map((option) => (
          <SelectDropdown
            key={option.id}
            name={option.label}
            id={option.id}
            value={option.selectedOptions}
            label={option.label}
            options={option.options}
            styles={dropdownStyles}
            onChange={option.onChange}
            multipleSelect={option.multipleSelect}
            data-qa={option.dataQa}
          />
        ))}
      </FilterV2>
    </div>
  );
};
